<template>
  <transition name="fade" appear>
    <BasePopup
      v-show="popupOpen"
      :popupOpen="popupOpen"
      :popup-title="$t('grondverzet.popupTitle')"
      @closeButtonClicked="close">
      <div class="grondverzet-content">
        <div v-if="initialLoading" class="grondverzet-skeleton">
          <div class="grondverzet-skeleton-band"></div>
          <div class="grondverzet-skeleton-body">
            <div class="grondverzet-skeleton-form">
              <span v-for="n in 8" :key="n" class="grondverzet-skeleton-row">
                <span class="grondverzet-skeleton-bar grondverzet-skeleton-bar--label"></span>
                <span class="grondverzet-skeleton-bar"></span>
              </span>
            </div>
            <div class="grondverzet-skeleton-map">{{ $t('grondverzet.checkingExisting') }}</div>
          </div>
        </div>

        <div v-else-if="unknownMelding" class="grondverzet-banner grondverzet-banner--error">
          <strong>{{ $t('grondverzet.errorMeldingUnknown') }}</strong>
        </div>

        <template v-else>
          <div class="grondverzet-band">
            <div class="grondverzet-band-cell">
              <span class="grondverzet-band-label">{{ $t('grondverzet.status') }}</span>
              <span class="grondverzet-chip" :class="canUpdate ? 'is-registered' : 'is-new'">
                <i class="grondverzet-chip-dot"></i>{{ statusLabel }}
              </span>
            </div>
            <template v-if="existingMelding">
              <div class="grondverzet-band-cell">
                <span class="grondverzet-band-label">{{ $t('grondverzet.referentieOVAM') }}</span>
                <span class="grondverzet-band-value is-mono">{{ existingMelding.referentieOVAM }}</span>
              </div>
              <div class="grondverzet-band-cell">
                <span class="grondverzet-band-label">{{ $t('grondverzet.datumCreatie') }}</span>
                <span class="grondverzet-band-value">{{ formatDate(existingMelding.datumCreatie) }}</span>
              </div>
              <div v-if="existingMelding.mistralUrl" class="grondverzet-band-cell">
                <span class="grondverzet-band-label">{{ $t('grondverzet.mistralUrl') }}</span>
                <a
                  class="grondverzet-band-link"
                  :href="existingMelding.mistralUrl"
                  target="_blank"
                  rel="noopener noreferrer">{{ $t('grondverzet.mistralOpen') }} &#8599;</a>
              </div>
            </template>
            <span v-else class="grondverzet-band-note">{{ $t('grondverzet.pendingOvam') }}</span>
          </div>

          <div class="grondverzet-layout">
            <div class="grondverzet-form">
              <div v-if="!credentialsPresent" class="grondverzet-banner grondverzet-banner--warn">
                <div>
                  <strong>{{ $t('grondverzet.credentialsMissingTitle') }}</strong>
                  <p>{{ $t('grondverzet.credentialsRequired') }}</p>
                </div>
                <button v-if="!showCredentials" type="button" class="grondverzet-banner-action" @click="toggleCredentials">
                  {{ $t('grondverzet.credentialsSetup') }}
                </button>
              </div>

              <div v-if="busy" class="grondverzet-banner grondverzet-banner--busy">
                {{ $t('grondverzet.busy') }}
              </div>
              <div
                v-else-if="result"
                class="grondverzet-banner"
                :class="result.ok ? 'grondverzet-banner--ok' : 'grondverzet-banner--error'">
                <div>
                  <strong>{{ result.ok ? $t(result.messageKey || 'grondverzet.resultOk') : errorMessage }}</strong>
                  <ul v-if="!result.ok && errorDetails.length" class="grondverzet-details">
                    <li v-for="(detail, index) in errorDetails" :key="index">{{ detail }}</li>
                  </ul>
                </div>
              </div>

              <section class="grondverzet-group">
                <h4>{{ $t('grondverzet.groupMelding') }}</h4>
                <div class="grondverzet-row">
                  <span class="grondverzet-label">
                    {{ $t('grondverzet.id') }}<FieldInfo info-key="grondverzet.info.id" />
                  </span>
                  <span class="grondverzet-field">
                    <span v-if="meldingId" class="grondverzet-value is-mono">{{ meldingId }}</span>
                    <span v-else class="grondverzet-value is-muted">{{ $t('grondverzet.idPlaceholder') }}</span>
                  </span>
                </div>
                <div class="grondverzet-row">
                  <span class="grondverzet-label">
                    {{ $t('grondverzet.titel') }}<FieldInfo info-key="grondverzet.info.titel" />
                  </span>
                  <span class="grondverzet-field">
                    <span class="grondverzet-value">{{ this.ovamTitel }}</span>
                  </span>
                </div>
              </section>

              <section class="grondverzet-group">
                <h4>{{ $t('grondverzet.groupReferenties') }}</h4>
                <div class="grondverzet-row">
                  <span class="grondverzet-label">
                    {{ $t('grondverzet.referentieEBSD') }}<span class="grondverzet-req">*</span><FieldInfo info-key="grondverzet.info.referentieEBSD" />
                  </span>
                  <input
                    v-model="referentieEBSD"
                    type="text"
                    class="grondverzet-input"
                    :class="{ 'is-empty': isMissing('referentieEBSD') }" />
                  <span v-if="isMissing('referentieEBSD')" class="grondverzet-row-hint">{{ $t('grondverzet.requiredHint') }}</span>
                </div>
                <div class="grondverzet-row">
                  <span class="grondverzet-label">
                    {{ $t('grondverzet.referentieInitiatiefnemer') }}<span class="grondverzet-req">*</span><FieldInfo info-key="grondverzet.info.referentieInitiatiefnemer" />
                  </span>
                  <span class="grondverzet-field">
                    <input
                      v-model="referentieInitiatiefnemer"
                      type="text"
                      class="grondverzet-input"
                      :class="{ 'is-empty': isMissing('referentieInitiatiefnemer') }" />
                    <span v-if="isMissing('referentieInitiatiefnemer')" class="grondverzet-row-hint">{{ $t('grondverzet.requiredHint') }}</span>
                  </span>
                </div>
                <div class="grondverzet-row">
                  <span class="grondverzet-label">
                    {{ $t('grondverzet.referentieIntegrator') }}<FieldInfo info-key="grondverzet.info.referentieIntegrator" />
                  </span>
                  <span class="grondverzet-field">
                    <span class="grondverzet-value">{{ request.referentieIntegrator }}</span>
                  </span>
                </div>
              </section>

              <section class="grondverzet-group">
                <h4>{{ $t('grondverzet.groupProjectzone') }}</h4>
                <div class="grondverzet-row">
                  <span class="grondverzet-label">
                    {{ $t('grondverzet.projectzoneType') }}<span class="grondverzet-req">*</span><FieldInfo info-key="grondverzet.info.projectzoneType" />
                  </span>
                  <span class="grondverzet-field">
                    <select
                      v-model="projectzoneType"
                      class="grondverzet-input"
                      :class="{ 'is-empty': isMissing('projectzoneType') }">
                      <option value="">{{ $t('grondverzet.projectzoneTypePlaceholder') }}</option>
                      <option v-for="option in projectzoneTypeOptions" :key="option" :value="option">
                        {{ $t('grondverzet.projectzoneTypeOptions.' + option) }}
                      </option>
                    </select>
                    <span v-if="isMissing('projectzoneType')" class="grondverzet-row-hint">{{ $t('grondverzet.requiredHint') }}</span>
                  </span>
                </div>
              </section>

              <section class="grondverzet-group">
                <h4>{{ $t('grondverzet.groupOnderzoek') }}</h4>
                <div class="grondverzet-row">
                  <span class="grondverzet-label">
                    {{ $t('grondverzet.vzmVeimNoodzakelijk') }}<span class="grondverzet-req">*</span><FieldInfo info-key="grondverzet.info.vzmVeimNoodzakelijk" />
                  </span>
                  <span class="grondverzet-field">
                    <template v-if="vzmLocked">
                      <span class="grondverzet-value-row">
                        <span class="grondverzet-value">{{ vzmLabel }}</span>
                        <span class="grondverzet-locked">
                          <i class="glyphicons glyphicons-lock"></i>{{ $t('grondverzet.vzmLockedNote') }}
                        </span>
                      </span>
                    </template>
                    <template v-else>
                      <span>
                        <label v-for="option in vzmOptions" :key="option.GcCode" class="grondverzet-radio">
                          <input v-model="vzmCode" type="radio" :value="option.GcCode" />
                          {{ option.GcDescription }}
                        </label>
                      </span>
                      <span v-if="isMissing('vzmVeimNoodzakelijk')" class="grondverzet-row-hint">{{ $t('grondverzet.requiredHint') }}</span>
                    </template>
                  </span>
                </div>
              </section>

              <div class="grondverzet-cred">
                <div class="grondverzet-cred-bar">
                  <i class="glyphicons glyphicons-lock"></i>
                  <strong>{{ $t('grondverzet.credentials') }}</strong>
                  <span class="grondverzet-cred-scope">{{ $t('grondverzet.credentialsScope') }}</span>
                  <button type="button" class="grondverzet-cred-toggle" @click="toggleCredentials">
                    {{ showCredentials ? $t('grondverzet.credentialsHide') : $t('grondverzet.credentialsManage') }}
                  </button>
                </div>
                <div v-if="showCredentials" class="grondverzet-cred-panel">
                  <div class="grondverzet-row">
                    <span class="grondverzet-label">{{ $t('grondverzet.clientId') }}</span>
                    <span class="grondverzet-field">
                      <input v-model="credClientId" type="text" class="grondverzet-input" autocomplete="off" />
                    </span>
                  </div>
                  <div class="grondverzet-row">
                    <span class="grondverzet-label">{{ $t('grondverzet.clientSecret') }}</span>
                    <span class="grondverzet-field">
                      <input
                        v-model="credClientSecret"
                        type="password"
                        class="grondverzet-input"
                        autocomplete="new-password"
                        :placeholder="credStatus && credStatus.hasClientSecret ? $t('grondverzet.secretPresent') : ''" />
                    </span>
                  </div>
                  <div class="grondverzet-row">
                    <span class="grondverzet-label">{{ $t('grondverzet.organisationNumber') }}</span>
                    <span class="grondverzet-field">
                      <input v-model="credOrganisationNumber" type="text" class="grondverzet-input" autocomplete="off" />
                    </span>
                  </div>
                  <div class="grondverzet-cred-actions">
                    <button type="button" class="grondverzet-cred-save" :disabled="!canSaveCredentials" @click="saveCredentials">
                      {{ $t('grondverzet.saveCredentials') }}
                    </button>
                    <span v-if="credBusy" class="grondverzet-cred-msg">{{ $t('grondverzet.busy') }}</span>
                    <span
                      v-else-if="credResult"
                      class="grondverzet-cred-msg"
                      :class="credResult.ok ? 'is-ok' : 'is-error'">
                      {{ credResult.ok ? $t('grondverzet.credentialsSaved') : settingsErrorMessage }}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div class="grondverzet-map-pane" role="group" :aria-label="$t('grondverzet.mapTitle')">
              <GrondverzetGeometryMap
                ref="zoneMap"
                :geometry="mapGeometry"
                :mode="mapMode"
                :center="projectPoint"
                :address="projectAddress"
                @drawn="onGeometryDrawn"
                @modified="onGeometryModified"
                @parcel-loading="parcelBusy = true"
                @parcel-hover="onParcelHover"
                @parcel="onParcelPicked" />

              <div class="grondverzet-map-search">
                <div class="grondverzet-map-search-box">
                  <input
                    v-model="searchQuery"
                    type="text"
                    class="grondverzet-map-search-input"
                    :placeholder="$t('grondverzet.searchPlaceholder')"
                    @input="clearSearchResult"
                    @keyup.enter="searchPlace" />
                  <button
                    type="button"
                    class="grondverzet-map-search-button"
                    :title="$t('grondverzet.searchPlaceholder')"
                    :disabled="searchBusy || !searchQuery.trim()"
                    @click="searchPlace">
                    <i class="glyphicons glyphicons-search"></i>
                  </button>
                </div>
                <p v-if="searchFailed" class="grondverzet-map-search-msg is-empty">{{ $t('grondverzet.searchNotFound') }}</p>
                <!-- What the search actually landed on: with the search limited to Belgium, a query
                     for somewhere else returns the nearest Belgian match. -->
                <p v-else-if="searchResult" class="grondverzet-map-search-msg">{{ searchResult }}</p>
              </div>

              <div v-if="mapMode" class="grondverzet-map-tools">
                <!-- Every picked parcel is saved straight away, so there is nothing to cancel there. -->
                <button type="button" class="grondverzet-tool is-text" @click="cancelMapMode">
                  {{ mapMode === 'parcel' ? $t('grondverzet.mapEditDone') : $t('button.cancel') }}
                </button>
              </div>
              <div v-else-if="mapGeometry && !editedGeometry" class="grondverzet-map-tools">
                <button type="button" class="grondverzet-tool is-text" @click="startPickingParcel">
                  <i class="glyphicons glyphicons-cadastral-map"></i>{{ $t('grondverzet.mapPickParcel') }}
                </button>
                <button type="button" class="grondverzet-tool is-text" @click="startDrawing">
                  <i class="glyphicons glyphicons-restart"></i>{{ $t('grondverzet.mapRedraw') }}
                </button>
                <button type="button" class="grondverzet-tool is-danger" :title="$t('grondverzet.mapClear')" @click="confirmClear = true">
                  <i class="glyphicons glyphicons-bin"></i>
                </button>
              </div>

              <div v-if="!mapGeometry && !mapMode" class="grondverzet-map-empty">
                <div class="grondverzet-map-empty-card">
                  <strong>{{ $t('grondverzet.mapEmptyTitle') }}</strong>
                  <p>{{ $t('grondverzet.mapEmptyBody') }}</p>
                  <div class="grondverzet-map-empty-actions">
                    <button type="button" class="grondverzet-map-draw" @click="startPickingParcel">{{ $t('grondverzet.mapPickParcel') }}</button>
                    <button type="button" class="grondverzet-map-draw is-secondary" @click="startDrawing">{{ $t('grondverzet.mapDraw') }}</button>
                  </div>
                </div>
              </div>

              <div v-if="confirmClear" class="grondverzet-map-overlay">
                <span>{{ $t('grondverzet.mapClearConfirm') }}</span>
                <button type="button" class="grondverzet-overlay-btn" @click="clearGeometry">{{ $t('button.confirm') }}</button>
                <button type="button" class="grondverzet-overlay-btn" @click="confirmClear = false">{{ $t('button.cancel') }}</button>
              </div>
              <div v-else-if="editedGeometry" class="grondverzet-map-overlay">
                <span>{{ $t('grondverzet.mapEditPending') }}</span>
                <button type="button" class="grondverzet-overlay-btn is-primary" @click="applyEdit">{{ $t('grondverzet.mapEditDone') }}</button>
                <button type="button" class="grondverzet-overlay-btn" @click="discardEdit">{{ $t('button.cancel') }}</button>
              </div>
              <div v-else-if="mapMode === 'draw'" class="grondverzet-map-overlay">
                <span>
                  {{ $t('grondverzet.mapDrawHint') }}
                  <template v-if="mapGeometry"> {{ $t('grondverzet.mapDrawReplaces') }}</template>
                </span>
              </div>
              <div v-else-if="mapMode === 'parcel'" class="grondverzet-map-overlay">
                <span v-if="parcelBusy">{{ $t('grondverzet.mapParcelBusy') }}</span>
                <span v-else-if="parcelMissed">{{ $t('grondverzet.mapParcelNone') }}</span>
                <span v-else-if="parcelTaken">
                  {{ $t(parcelTaken.removed ? 'grondverzet.mapParcelRemoved' : 'grondverzet.mapParcelTaken', [parcelTaken.capakey]) }}
                </span>
                <span v-else-if="parcelHover">
                  {{ $t(parcelHoverInZone ? 'grondverzet.mapParcelHoverRemove' : 'grondverzet.mapParcelHover', [parcelHover]) }}
                </span>
                <span v-else>{{ $t('grondverzet.mapPickParcelHint') }}</span>
              </div>
              <!-- Teaches the drag interaction before the user tries it; the handles alone do not
                   explain adding or removing a corner. -->
              <div v-else-if="mapGeometry" class="grondverzet-map-tip">{{ $t('grondverzet.mapEditHint') }}</div>
            </div>
          </div>
        </template>
      </div>

      <template #footer>
        <div class="grondverzet-footer">
          <button type="button" class="grondverzet-footer-close" @click="close">{{ $t('grondverzet.close') }}</button>
          <div class="grondverzet-footer-right">
            <span v-if="primaryReason" class="grondverzet-footer-reason">{{ primaryReason }}</span>
            <button type="button" class="grondverzet-footer-primary" :disabled="!primaryEnabled" @click="runPrimaryAction">
              {{ primaryLabel }}
            </button>
          </div>
        </div>
      </template>
    </BasePopup>
  </transition>
</template>

<script>
import BasePopup from '@/components/boxes/base-components/basePopup.vue'
import GrondverzetGeometryMap from '@/components/grondverzet/grondverzetGeometryMap.vue'
import { GrondverzetService } from '@/services/DataWS/grondverzetService'
import { CodeListService } from '@/services/DataWS/codeListService'
import { getProjectAddressQuery } from '@/services/addressSearch/addressSearchService'
import {
  buildMeldingRequest,
  getMeldingId,
  getProjectPoint,
  setProjectGeometry,
  setMeldingId,
  setMistralUrl,
  PROJECTZONE_TYPE_FIELD,
  REFERENTIE_INITIATIEFNEMER_FIELD,
  VZM_VEIM_FIELD,
  VZM_VEIM_CATEGORY_ID,
  PROJECTZONE_TYPE_OPTIONS
} from '@/utils/grondverzet/grondverzetMapping'

// Info icon with the field explanation as its hover tooltip. The tooltip follows the user's
// language now that the info texts exist in every locale.
const FieldInfo = {
  functional: true,
  props: { infoKey: { type: String, default: '' } },
  render(h, ctx) {
    const text = ctx.parent.$t(ctx.props.infoKey)
    return h('span', {
      class: 'grondverzet-info',
      attrs: { title: text, tabindex: 0 }
    }, [h('i', { class: 'glyphicons glyphicons-info-sign' })])
  }
}

const grondverzetService = new GrondverzetService()
const codeListService = new CodeListService()

export default {
  name: 'GrondverzetPopup',
  components: { BasePopup, FieldInfo, GrondverzetGeometryMap },
  props: {
    popupOpen: {
      type: Boolean,
      required: true
    },
    project: {
      type: Object,
      required: false,
      default: null
    }
  },
  data() {
    return {
      busy: false,
      initialLoading: false,
      result: null,
      successTimer: null,
      existingMelding: null,
      dirtyCheckBaseline: null,
      projectzoneTypeOptions: PROJECTZONE_TYPE_OPTIONS,
      vzmOptions: [],
      // '' | 'draw' | 'parcel' - whether the user is drawing or picking parcels (reshaping needs no mode).
      mapMode: '',
      // Contour being reshaped; only written to the project when the user keeps it.
      editedGeometry: null,
      confirmClear: false,
      searchQuery: '',
      searchBusy: false,
      searchFailed: false,
      searchResult: '',
      parcelBusy: false,
      parcelMissed: false,
      // CAPAKEY of the parcel under the pointer while picking, and whether a click would remove it.
      parcelHover: '',
      parcelHoverInZone: false,
      // { capakey, removed } of the parcel just picked, shown until the pointer moves to another one.
      parcelTaken: null,
      // Per-license OVAM settings. credStatus comes from the server:
      // { clientId, hasClientId, hasClientSecret, organisationNumber, hasOrganisationNumber } - never the secret.
      showCredentials: false,
      credStatus: null,
      credClientId: '',
      credClientSecret: '',
      credOrganisationNumber: '',
      credBusy: false,
      credResult: null,
      referentieEBSD: (this.project && this.project['PrCode']) ? this.project['PrCode'] : '',
      ovamTitel: (this.project && this.project['PrName']) ? this.project['PrName'] : ''
    }
  },
  created() {
    this.loadVzmOptions()
    this.loadCredentialStatus(true)
    if (this.meldingId) {
      this.loadExisting({ initial: true })
    }
  },
  mounted() {
    // BasePopup only wires Escape when it renders its own footer, and this popup fills the footer slot.
    window.addEventListener('keyup', this.onKeyUp)
  },
  computed: {
    // After an error the action buttons are disabled to prevent spamming failing calls.
    hasError() {
      return !!(this.result && this.result.ok === false)
    },
    // We have a melding id, but OVAM returned 404: the registration is unknown there. This is an
    // inconsistent state the user cannot resolve, so we show a service-desk message instead of the form.
    unknownMelding() {
      return !!(this.result && this.result.unknownMelding)
    },
    // OVAM's own phase once we have the melding; before that just registered / not registered.
    statusLabel() {
      if (this.existingMelding && this.existingMelding.fase) {
        // The list of phases is an enum defined in the documentation, even though they're human-readable (in Dutch)
        // this allows translation
        return this.$t('grondverzet.phases["' + this.existingMelding.fase + '"]')
      }
      return this.canUpdate ? this.$t('grondverzet.statusRegistered') : this.$t('grondverzet.statusNew')
    },
    // The per-license OVAM settings (client id, secret and default EBSD) must all be configured before
    // a melding can be registered/updated.
    credentialsPresent() {
      return !!(this.credStatus && this.credStatus.hasClientId && this.credStatus.hasClientSecret && this.credStatus.hasOrganisationNumber)
    },
    // Save is allowed once id + EBSD are entered and either a new secret is typed or one already
    // exists (a blank secret keeps the existing one).
    canSaveCredentials() {
      const hasSecret = !!(this.credClientSecret && this.credClientSecret.trim()) ||
        !!(this.credStatus && this.credStatus.hasClientSecret)
      return !this.credBusy &&
        !!(this.credClientId && this.credClientId.trim()) &&
        !!(this.credOrganisationNumber && this.credOrganisationNumber.trim()) &&
        hasSecret
    },
    settingsErrorMessage() {
      // Any failure of the settings call surfaces as one general "problem retrieving the settings"
      // message; this endpoint does not call OVAM, so no OVAM-status-specific messaging.
      return this.$t('grondverzet.credentialsError')
    },
    // Show a status-based message instead of OVAM's raw response body, which can be an
    // HTML error page (e.g. a proxy "Service unavailable") that must not leak into the UI.
    errorMessage() {
      const status = this.result && this.result.status
      return this.$t('grondverzet.' + this.errorKeyForStatus(status))
    },
    // True if the submitted data was bad. In this case we don't want to disable the submit button,
    // because the user may want to correct the data and submit again.
    errorInvalidData() {
      const status = this.result && this.result.status
      return status === 400
    },
    // Concrete validation reasons the backend passes through for a 4xx (OVAM's
    // constraint-violations, or our own missing-fields list). Shown on top of the
    // generic status message so the user knows what to fix.
    errorDetails() {
      const body = this.result && this.result.body
      if (!body || typeof body !== 'object') {
        return []
      }
      const violations = body['constraint-violations']
      if (Array.isArray(violations)) {
        return violations.map((v) => v && v.message).filter(Boolean)
      }
      if (Array.isArray(body.missing)) {
        return body.missing
      }
      return []
    },
    meldingId() {
      return getMeldingId(this.project)
    },
    canUpdate() {
      return !!this.meldingId
    },
    // Updating only makes sense when an input differs from what the last GET returned (baseline).
    // Mirrors the fetched melding -> not dirty -> button greyed; change an input -> dirty; revert -> greyed again.
    isDirty() {
      if (!this.dirtyCheckBaseline) {
        return false
      }
      const now = this.currentFormSnapshot()
      // Geometry counts only when OVAM returned one (baseline !== null); otherwise we cannot
      // compare and skip it, so an absent upstream geometry does not force a permanent dirty state.
      const geometryChanged = this.dirtyCheckBaseline.geometrie !== null && now.geometrie !== this.dirtyCheckBaseline.geometrie
      return now.referentieEBSD !== this.dirtyCheckBaseline.referentieEBSD ||
        now.referentieInitiatiefnemer !== this.dirtyCheckBaseline.referentieInitiatiefnemer ||
        now.projectzoneType !== this.dirtyCheckBaseline.projectzoneType ||
        now.vzmCode !== this.dirtyCheckBaseline.vzmCode ||
        geometryChanged
    },
    referentieInitiatiefnemer: {
      get() {
        return (this.project && this.project[REFERENTIE_INITIATIEFNEMER_FIELD]) || ''
      },
      set(value) {
        if (this.project) {
          this.$set(this.project, REFERENTIE_INITIATIEFNEMER_FIELD, value)
        }
      }
    },
    projectzoneType: {
      get() {
        const raw = this.project && this.project[PROJECTZONE_TYPE_FIELD]
        // Default to the "Selecteer een type" placeholder unless a valid prior choice is stored.
        return PROJECTZONE_TYPE_OPTIONS.includes(raw) ? raw : ''
      },
      set(value) {
        if (this.project) {
          this.$set(this.project, PROJECTZONE_TYPE_FIELD, value)
        }
      }
    },
    // The selected code list value ('true'/'false') for PrOvamPrecautionsRequiredCode. Empty = not
    // chosen yet (required). The label shown per option comes from the code list (GcDescription).
    vzmCode: {
      get() {
        const raw = this.project && this.project[VZM_VEIM_FIELD]
        return raw == null ? '' : String(raw)
      },
      set(value) {
        if (this.project) {
          this.$set(this.project, VZM_VEIM_FIELD, value)
        }
      }
    },
    // VZM/VEIM is one-way and cannot be changed once the melding is registered.
    vzmLocked() {
      return this.canUpdate
    },
    // Code list label of the locked choice, so the read-only value reads like the radio did.
    vzmLabel() {
      const option = this.vzmOptions.find((o) => String(o.GcCode) === this.vzmCode)
      return option ? option.GcDescription : this.vzmCode
    },
    request() {
      // 'Referentie EBSD' is normally the project code but the user is allowed to change it
      return buildMeldingRequest(this.project, { referentieEBSD: this.referentieEBSD, titel: this.ovamTitel })
    },
    // The projectzone geometry as sent to OVAM; empty when the project has no contour yet, in which
    // case the map starts out empty and the user can draw one.
    mapGeometry() {
      return this.request.projectzone.geometrie || ''
    },
    projectPoint() {
      return getProjectPoint(this.project)
    },
    projectAddress() {
      return getProjectAddressQuery(this.project)
    },
    missingRequired() {
      const r = this.request
      const checks = [
        { key: 'titel', value: r.titel },
        { key: 'referentieEBSD', value: r.referentieEBSD },
        { key: 'referentieInitiatiefnemer', value: r.referentieInitiatiefnemer },
        { key: 'referentieIntegrator', value: r.referentieIntegrator },
        { key: 'projectzoneType', value: r.projectzone.type },
        { key: 'geometrie', value: r.projectzone.geometrie }
      ]
      const missing = checks.filter((c) => !c.value || !String(c.value).trim()).map((c) => c.key)
      if (!this.vzmCode) {
        missing.push('vzmVeimNoodzakelijk')
      }
      return missing
    },
    // Registering and updating are mutually exclusive, so the footer carries one action.
    primaryLabel() {
      return this.canUpdate ? this.$t('grondverzet.updaten') : this.$t('grondverzet.aanmelden')
    },
    primaryEnabled() {
      if (this.errorInvalidData) {
        return true // allow user to correct invalid data
      }
      if (!this.credentialsPresent || this.busy || this.hasError || this.missingRequired.length) {
        return false
      }
      return this.canUpdate ? this.isDirty : true
    },
    // Why the action is unavailable, next to the button instead of in a block below the form.
    primaryReason() {
      if (this.busy || this.hasError) {
        return ''
      }
      if (!this.credentialsPresent) {
        return this.$t('grondverzet.reasonNoCredentials')
      }
      if (this.missingRequired.length) {
        const labels = this.missingRequired.map((key) => this.$t('grondverzet.' + key))
        return this.$t('grondverzet.reasonMissing', [labels.join(', ')])
      }
      if (this.canUpdate && !this.isDirty) {
        return this.$t('grondverzet.reasonNoChanges')
      }
      return ''
    }
  },
  methods: {
    close() {
      this.$emit('closeButtonClicked')
    },
    onKeyUp(event) {
      if (!this.popupOpen || event.keyCode !== 27) {
        return
      }
      // Escape backs out of the map first, so it cannot throw away work in progress.
      if (this.editedGeometry) {
        this.discardEdit()
        return
      }
      if (this.mapMode || this.confirmClear) {
        this.setMapMode('')
        return
      }
      this.close()
    },
    isMissing(key) {
      return this.missingRequired.includes(key)
    },
    runPrimaryAction() {
      if (!this.primaryEnabled) {
        return
      }
      if (this.canUpdate) {
        this.update()
      } else {
        this.submit()
      }
    },
    // Map an HTTP status to a friendly, translated message key. Anything without a
    // specific mapping (including undefined for a network failure) falls back to the
    // generic servicedesk message, so OVAM's raw body is never shown.
    errorKeyForStatus(status) {
      if (status === 400) return 'errorBadRequest'
      if (status === 401 || status === 403) return 'errorUnauthorized'
      if (status === 404) return 'errorNotFound'
      if (status === 409) return 'errorConflict'
      if (status === 502 || status === 503 || status === 504) return 'errorUnavailable'
      if (status >= 500) return 'errorServer'
      return 'errorContactServicedesk'
    },
    async loadVzmOptions() {
      try {
        this.vzmOptions = await codeListService.getCodeListByCategoryIDOrCode(VZM_VEIM_CATEGORY_ID)
      } catch (error) {
        this.vzmOptions = []
      }
    },
    async loadCredentialStatus(initial) {
      this.credBusy = true
      try {
        const status = await grondverzetService.getCredentialStatus()
        this.credStatus = status
        this.credClientId = (status && status.clientId) || ''
        this.credOrganisationNumber = (status && status.organisationNumber) || ''

        this.credClientSecret = ''
        // On first open, open the section when credentials are missing so the user fills them in.
        if (initial) {
          this.showCredentials = !this.credentialsPresent
        }
      } catch (error) {
        this.credStatus = null
        if (initial) {
          this.showCredentials = true
          this.credResult = {
            ok: false,
            status: error && error.status ? error.status : undefined
          }
        }
      } finally {
        this.credBusy = false
      }
    },
    async saveCredentials() {
      if (!this.canSaveCredentials) {
        return
      }
      this.credBusy = true
      this.credResult = null
      try {
        // A blank secret keeps the existing one (see backend); id + organisation number are always sent.
        await grondverzetService.saveCredentials({
          clientId: this.credClientId.trim(),
          clientSecret: this.credClientSecret,
          organisationNumber: this.credOrganisationNumber
        })
        this.credClientSecret = ''
        await this.loadCredentialStatus()
        this.credResult = { ok: true }
      } catch (error) {
        this.credResult = {
          ok: false,
          status: error && error.status ? error.status : undefined,
          body: this.parseErrorBody(error)
        }
      } finally {
        this.credBusy = false
      }
    },
    toggleCredentials() {
      this.showCredentials = !this.showCredentials
    },
    async searchPlace() {
      if (this.searchBusy || !this.searchQuery.trim() || !this.$refs.zoneMap) {
        return
      }
      this.searchBusy = true
      this.clearSearchResult()
      try {
        this.searchResult = await this.$refs.zoneMap.searchPlace(this.searchQuery)
        this.searchFailed = !this.searchResult
      } finally {
        this.searchBusy = false
      }
    },
    clearSearchResult() {
      this.searchResult = ''
      this.searchFailed = false
    },
    startDrawing() {
      this.setMapMode('draw')
    },
    startPickingParcel() {
      this.setMapMode('parcel')
    },
    cancelMapMode() {
      this.setMapMode('')
    },
    setMapMode(mode) {
      this.confirmClear = false
      this.editedGeometry = null
      this.parcelBusy = false
      this.parcelMissed = false
      this.parcelHover = ''
      this.parcelHoverInZone = false
      this.parcelTaken = null
      this.mapMode = mode
    },
    onParcelHover(capakey, inZone) {
      this.parcelHover = capakey
      this.parcelHoverInZone = !!inZone
      if (capakey) {
        this.parcelMissed = false
      }
      if (this.parcelTaken && this.parcelTaken.capakey !== capakey) {
        this.parcelTaken = null
      }
    },
    // Each picked parcel is added to (or cut out of) the zone and saved right away; the mode stays on
    // so the user can keep picking. The CAPAKEY stays on screen so the user sees which parcel they hit.
    onParcelPicked(parcel) {
      this.parcelBusy = false
      if (!parcel) {
        this.parcelMissed = true
        return
      }
      this.parcelMissed = false
      this.parcelTaken = { capakey: parcel.capakey, removed: parcel.removed }
      this.saveGeometry(parcel.contour)
    },
    // A drawn zone is the project's own contour (PrPolygonGeometry), so it is stored on the project
    // and persisted through the parent's project save - the same route as the melding id.
    onGeometryDrawn(wkt) {
      this.setMapMode('')
      this.saveGeometry(wkt)
    },
    // A dragged corner is not stored yet: it puts the map in a pending state with keep/discard.
    onGeometryModified(wkt) {
      this.editedGeometry = wkt
    },
    // Reshaping saves once, on confirm, instead of on every dragged corner.
    applyEdit() {
      const wkt = this.editedGeometry
      this.editedGeometry = null
      if (wkt) {
        this.saveGeometry(wkt)
      }
    },
    discardEdit() {
      this.editedGeometry = null
      // The map still shows the reshaped zone, so ask it to draw the stored one again.
      if (this.$refs.zoneMap) {
        this.$refs.zoneMap.resetGeometry()
      }
    },
    clearGeometry() {
      this.setMapMode('')
      this.saveGeometry('')
    },
    saveGeometry(wkt) {
      setProjectGeometry(this.project, wkt)
      this.$emit('geometryChanged')
    },
    // vue-resource's `.body` is always the raw response text — it only auto-parses into
    // `.data`, and only when the header is exactly "application/json" (an RFC7807
    // "application/problem+json" error response, like OVAM's constraint-violations body,
    // never matches). Parse it ourselves so errorDetails can read the structured body.
    parseErrorBody(error) {
      const raw = error && error.body
      if (!raw) {
        return null
      }
      if (typeof raw === 'object') {
        return raw
      }
      try {
        return JSON.parse(raw)
      } catch (e) {
        return null
      }
    },
    async loadExisting(options) {
      const initial = !!(options && options.initial)
      this.busy = true
      if (initial) {
        this.initialLoading = true
      }
      try {
        const melding = await grondverzetService.getMelding(this.meldingId)
        this.existingMelding = melding
        this.applyMeldingToForm(melding)
      } catch (error) {
        const status = error && error.status ? error.status : undefined
        this.result = {
          ok: false,
          status,
          body: this.parseErrorBody(error),
          unknownMelding: status === 404
        }
      } finally {
        this.busy = false
        this.initialLoading = false
      }
    },
    applyMeldingToForm(melding) {
      if (!melding) {
        return
      }
      this.referentieEBSD = melding.referentieEBSD || ''
      this.ovamTitel = melding.titel || ''
      this.referentieInitiatiefnemer = melding.referentieInitiatiefnemer || ''
      if (melding.projectzone && melding.projectzone.type != null) {
        this.projectzoneType = melding.projectzone.type
      }
      this.vzmCode = melding.vzmVeimNoodzakelijk == null ? '' : String(melding.vzmVeimNoodzakelijk)
      // Baseline = what OVAM currently has. The editable fields are set from the melding above, but the
      // geometry is derived from the project (not written back), so take its baseline straight from the
      // melding: a project polygon that differs from OVAM's then makes the form dirty (enables Updaten).
      const meldingGeometry = melding.projectzone && melding.projectzone.geometrie
      this.dirtyCheckBaseline = {
        referentieEBSD: melding.referentieEBSD || '',
        referentieInitiatiefnemer: melding.referentieInitiatiefnemer || '',
        projectzoneType: (melding.projectzone && melding.projectzone.type) || '',
        vzmCode: melding.vzmVeimNoodzakelijk == null ? '' : String(melding.vzmVeimNoodzakelijk),
        // OVAM echoes the geometry back verbatim in our exact WKT format (verified), so a direct
        // string compare is enough; just trim to be safe.
        geometrie: meldingGeometry ? String(meldingGeometry).trim() : null
      }
    },
    currentFormSnapshot() {
      return {
        referentieEBSD: this.licenseEbsd,
        referentieInitiatiefnemer: this.referentieInitiatiefnemer || '',
        projectzoneType: this.projectzoneType || '',
        vzmCode: this.vzmCode || '',
        geometrie: String(this.request.projectzone.geometrie || '').trim()
      }
    },
    async submit() {
      // Guard against a double registration (there is no way to cancel a melding at OVAM).
      if (this.missingRequired.length || this.canUpdate || !this.credentialsPresent) {
        return
      }
      await this.execute(() => grondverzetService.submitMelding(this.request), (response) => {
        // Set the returned Mistral URL + id on the project first, then signal the parent to persist it.
        if (response && response.mistralUrl) {
          setMistralUrl(this.project, response.mistralUrl)
        }
        const id = response && (response.id || response.grondverzetsonderzoekMeldingId)
        if (id != null) {
          setMeldingId(this.project, id)
          this.$emit('meldingIdChanged', id.toString())
        }
      }, 'grondverzet.resultOkCreate')
      // After creating, show the result from a GET (the melding summary always comes from the GET).
      if (this.result && this.result.ok && this.meldingId) {
        await this.loadExisting()
      }
    },
    async update() {
      if (!this.canUpdate || !this.isDirty || this.missingRequired.length || !this.credentialsPresent) {
        return
      }
      await this.execute(() => grondverzetService.updateMelding(this.meldingId, this.request), null, 'grondverzet.resultOkUpdate')
      if (this.result && this.result.ok) {
        await this.loadExisting()
      }
    },
    async execute(call, onSuccess, successKey) {
      this.busy = true
      this.result = null
      this.clearSuccessTimer()
      try {
        const response = await call()
        if (onSuccess) {
          onSuccess(response)
        }
        this.result = { ok: true, messageKey: successKey || 'grondverzet.resultOk' }
        this.scheduleSuccessDismiss()
      } catch (error) {
        // vue-resource rejects with a response-like object on a non-2xx status.
        this.result = {
          ok: false,
          status: error && error.status ? error.status : undefined,
          body: this.parseErrorBody(error)
        }
      } finally {
        this.busy = false
      }
    },
    formatDate(value) {
      if (!value) {
        return ''
      }
      const date = new Date(value)
      return isNaN(date.getTime()) ? String(value) : date.toLocaleString('NL-nl')
    },
    scheduleSuccessDismiss() {
      this.clearSuccessTimer()
      this.successTimer = setTimeout(() => {
        if (this.result && this.result.ok) {
          this.result = null
        }
        this.successTimer = null
      }, 5000)
    },
    clearSuccessTimer() {
      if (this.successTimer) {
        clearTimeout(this.successTimer)
        this.successTimer = null
      }
    }
  },
  beforeDestroy() {
    window.removeEventListener('keyup', this.onKeyUp)
    this.clearSuccessTimer()
  }
}
</script>

<style lang="less" scoped>
@formWidth: 560px;
@mapWidth: 520px;

.grondverzet-content {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  width: 1120px;
  max-width: 100%;
  min-height: 0;
  color: black;
}

// The popup body is capped at 80vh (see BasePopup). The content fills that cap and only the form
// column scrolls, so the map keeps the full height instead of leaving whitespace below it.
/deep/ .popupContent {
  min-height: 0;
  overflow: hidden;
}

// Stack the shared header's title and project name, and keep the project name secondary.
// !important because BasePopup styles these two itself (row layout, 18px) from its own scope,
// with a longer selector than a /deep/ override from here can reach.
/deep/ .popupHeader {
  padding: 12px 15px;

  .popup-title-container {
    flex-direction: column !important;
    align-items: flex-start !important;
    gap: 0 !important;
  }

  .popup-sub-title {
    align-self: flex-start !important;
    font-size: 12px !important;
    opacity: 0.85;
  }
}

/* status band */
.grondverzet-band {
  display: flex;
  align-items: center;
  gap: 30px;
  flex: 0 0 auto;
  padding: 9px 20px;
  background-color: @gray50;
  border-bottom: 1px solid @gray200;
}

.grondverzet-band-cell {
  display: flex;
  flex-direction: column;
}

.grondverzet-band-label {
  font-size: 10.5px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: @gray500;
}

.grondverzet-band-value {
  font-size: 13px;
  font-weight: 600;
}

.is-mono {
  font-family: Consolas, "Courier New", monospace;
  letter-spacing: 0.02em;
}

.grondverzet-band-link {
  font-size: 13px;
  font-weight: 600;
  color: @blue600;
}

.grondverzet-band-note {
  font-size: 12px;
  color: @gray500;
}

.grondverzet-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 10px;
  font-size: 11.5px;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  border-radius: 999px;

  &.is-registered {
    color: @greenTerra900;
    background-color: @greenTerra50;
    border: 1px solid @greenTerra300;
  }

  &.is-new {
    color: @gray600;
    background-color: @gray100;
    border: 1px solid @gray200;
  }
}

.grondverzet-chip-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: currentColor;
}

/* two columns */
.grondverzet-layout {
  display: flex;
  align-items: stretch;
  flex: 1 1 auto;
  min-height: 470px;
}

.grondverzet-form {
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex: 1 1 @formWidth;
  min-width: 0;
  padding: 14px 20px 16px;
  overflow-y: auto;
}

.grondverzet-map-pane {
  position: relative;
  flex: 0 0 @mapWidth;
  border-left: 1px solid @gray200;
  overflow: hidden;
}

/* field groups */
.grondverzet-group {
  h4 {
    margin: 0 0 4px;
    font-size: 10.5px;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: @gray500;
  }

  & + .grondverzet-group {
    margin-top: 6px;
  }
}

.grondverzet-row {
  display: grid;
  grid-template-columns: 190px 1fr;
  gap: 12px;
  align-items: baseline;
  padding: 3px 0;
}

.grondverzet-label {
  color: @gray700;
}

.grondverzet-req {
  margin-left: 2px;
  color: @greenTerra800;
  font-weight: 700;
}

.grondverzet-field {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}

.grondverzet-value {
  font-weight: 500;
  color: @gray900;
}

.is-muted {
  color: @gray500;
  font-weight: 400;
}

.grondverzet-row-hint {
  font-size: 11.5px;
  color: @warning700;
}

// Keeps a value and its note on one line, wrapping only when the column gets too narrow.
.grondverzet-value-row {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 4px 8px;
}

.grondverzet-locked {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11.5px;
  color: @gray500;
}

// Glyphicons ship with padding on the pseudo element, which does not fit inline labels or the
// small icon buttons on the map.
.grondverzet-locked .glyphicons,
.grondverzet-cred-bar .glyphicons {
  font-size: 12px;

  &:before {
    padding: 0;
  }
}

.grondverzet-input {
  width: 100%;
  padding: 5px 9px;
  color: @gray900;
  background-color: white;
  border: 1px solid @gray300;
  border-radius: 6px;

  &:focus {
    outline: none;
    border-color: @primaryGreen;
    box-shadow: 0 0 0 3px rgba(123, 199, 66, 0.25);
  }

  &.is-empty {
    border-color: @warning200;
    background-color: @warning50;
  }
}

.grondverzet-info {
  margin-left: 4px;
  color: @gray400;
  font-size: 12px;
  font-weight: normal;
  cursor: help;
}

.grondverzet-radio {
  display: inline-block;
  margin-right: 16px;
  font-weight: normal;
  cursor: pointer;

  input {
    margin-right: 4px;
  }
}

/* banners */
.grondverzet-banner {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 12px;
  font-size: 13px;
  border-radius: 8px;

  p {
    margin: 2px 0 0;
    font-size: 12px;
  }

  &--warn {
    color: @warning800;
    background-color: @warning50;
    border: 1px solid @warning200;

    strong {
      color: @warning700;
    }
  }

  &--busy {
    color: @gray600;
    background-color: @gray100;
  }

  &--ok {
    color: @greenTerra900;
    background-color: @greenTerra50;
  }

  &--error {
    color: @error700;
    background-color: @error50;
  }
}

.grondverzet-banner-action {
  margin-left: auto;
  flex: 0 0 auto;
  padding: 5px 10px;
  font-size: 12px;
  font-weight: 600;
  color: @warning700;
  background-color: white;
  border: 1px solid @warning200;
  border-radius: 6px;
}

.grondverzet-details {
  margin: 4px 0 0;
  padding-left: 18px;
  font-size: 12px;
}

/* credentials, license wide */
.grondverzet-cred {
  margin-top: auto;
  background-color: @gray25;
  border: 1px solid @gray200;
  border-radius: 8px;
}

.grondverzet-cred-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  color: @gray600;

  strong {
    font-size: 12.5px;
    color: @gray800;
  }
}

.grondverzet-cred-scope {
  font-size: 11px;
  color: @gray500;
}

.grondverzet-cred-toggle {
  margin-left: auto;
  padding: 4px 9px;
  font-size: 12px;
  font-weight: 600;
  color: @gray600;
  background-color: white;
  border: 1px solid @gray300;
  border-radius: 6px;
}

.grondverzet-cred-panel {
  padding: 10px 12px 12px;
  border-top: 1px solid @gray200;

  .grondverzet-row {
    grid-template-columns: 120px 1fr;
    padding: 4px 0;
  }
}

.grondverzet-cred-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 8px;
}

.grondverzet-cred-save {
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 600;
  color: white;
  background-color: @greenTerra700;
  border: 1px solid @greenTerra700;
  border-radius: 6px;

  &:disabled {
    background-color: @gray300;
    border-color: @gray300;
    cursor: not-allowed;
  }
}

.grondverzet-cred-msg {
  font-size: 11.5px;
  color: @gray500;

  &.is-ok {
    color: @greenTerra800;
  }

  &.is-error {
    color: @error600;
  }
}

/* map overlays */
// Clear of the OpenLayers zoom control, which sits at the top left of the map.
.grondverzet-map-search {
  position: absolute;
  top: 12px;
  left: 48px;
  z-index: 5;
  width: 210px;
}

.grondverzet-map-search-box {
  display: flex;
  align-items: center;
  background-color: rgba(255, 255, 255, 0.94);
  border: 1px solid @gray300;
  border-radius: 6px;
  box-shadow: 0 1px 3px rgba(16, 24, 40, 0.12);
  overflow: hidden;
}

.grondverzet-map-search-input {
  flex: 1 1 auto;
  min-width: 0;
  padding: 4px 8px;
  font-size: 12px;
  color: @gray900;
  background: none;
  border: none;

  &:focus {
    outline: none;
  }
}

.grondverzet-map-search-button {
  flex: 0 0 auto;
  width: 26px;
  height: 26px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: @gray600;
  background: none;
  border: none;
  border-left: 1px solid @gray200;

  &:hover:not(:disabled),
  &:focus:not(:disabled) {
    background-color: @gray100;
  }

  &:disabled {
    color: @gray400;
    cursor: not-allowed;
  }

  .glyphicons {
    font-size: 12px;

    &:before {
      padding: 0;
    }
  }
}

.grondverzet-map-search-msg {
  margin: 4px 0 0;
  padding: 3px 8px;
  font-size: 11.5px;
  color: @gray600;
  background-color: rgba(255, 255, 255, 0.94);
  border: 1px solid @gray200;
  border-radius: 6px;

  &.is-empty {
    color: @warning800;
    background-color: @warning50;
    border-color: @warning200;
  }
}

.grondverzet-map-tools {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 5;
  display: flex;
  gap: 1px;
  padding: 2px;
  background-color: rgba(255, 255, 255, 0.94);
  border: 1px solid @gray300;
  border-radius: 6px;
  box-shadow: 0 1px 3px rgba(16, 24, 40, 0.12);
}

.grondverzet-tool {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  width: 24px;
  height: 24px;
  font-size: 11.5px;
  font-weight: 600;
  color: @gray700;
  background: none;
  border: none;
  border-radius: 4px;

  &:hover,
  &:focus {
    background-color: @gray100;
  }

  &.is-text {
    width: auto;
    padding: 0 8px;
  }

  &.is-active {
    color: white;
    background-color: @greenTerra700;

    &:hover,
    &:focus {
      background-color: @greenTerra800;
    }
  }

  &.is-danger {
    color: @error700;
  }

  .glyphicons {
    font-size: 13px;

    &:before {
      padding: 0;
    }
  }
}

.grondverzet-map-empty {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 4;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(249, 250, 251, 0.7);
}

.grondverzet-map-empty-card {
  max-width: 300px;
  padding: 16px 20px;
  text-align: center;
  background-color: white;
  border: 1px dashed @gray300;
  border-radius: 10px;
  box-shadow: 0 4px 14px rgba(16, 24, 40, 0.08);

  strong {
    display: block;
    font-size: 13.5px;
  }

  p {
    margin: 4px 0 12px;
    font-size: 12px;
    color: @gray500;
  }
}

.grondverzet-map-empty-actions {
  display: flex;
  justify-content: center;
  gap: 8px;
}

.grondverzet-map-draw {
  padding: 7px 14px;
  font-size: 13px;
  font-weight: 600;
  color: white;
  background-color: @greenTerra700;
  border: 1px solid @greenTerra700;
  border-radius: 6px;

  &.is-secondary {
    color: @gray700;
    background-color: white;
    border-color: @gray300;
  }
}

.grondverzet-map-overlay {
  position: absolute;
  left: 12px;
  right: 12px;
  bottom: 12px;
  z-index: 5;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  font-size: 12px;
  color: white;
  background-color: rgba(29, 41, 57, 0.92);
  border-radius: 8px;
}

.grondverzet-overlay-btn {
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 600;
  color: @gray800;
  background-color: white;
  border: 1px solid @gray300;
  border-radius: 6px;

  &:first-of-type {
    margin-left: auto;
  }

  &.is-primary {
    color: white;
    background-color: @greenTerra700;
    border-color: @greenTerra700;
  }
}

// Quiet, always-on hint at the bottom of the map: it explains the drag interaction without taking
// over the overlay slot that the pending/drawing states use.
.grondverzet-map-tip {
  position: absolute;
  left: 12px;
  right: 12px;
  bottom: 12px;
  z-index: 5;
  padding: 5px 10px;
  font-size: 11.5px;
  color: @gray600;
  background-color: rgba(255, 255, 255, 0.88);
  border: 1px solid @gray200;
  border-radius: 6px;
}

/* footer, rendered through the BasePopup footer slot */
.grondverzet-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 18px;
  background-color: @gray25;
  border-top: 1px solid @gray200;
  border-bottom-left-radius: 6px;
  border-bottom-right-radius: 6px;
}

.grondverzet-footer-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.grondverzet-footer-reason {
  font-size: 12px;
  color: @gray500;
  text-align: right;
}

.grondverzet-footer-close {
  padding: 8px 14px;
  font-size: 13px;
  font-weight: 600;
  color: @gray700;
  background-color: white;
  border: 1px solid @gray300;
  border-radius: 6px;

  &:hover,
  &:focus {
    background-color: @gray100;
  }
}

.grondverzet-footer-primary {
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 600;
  color: white;
  background-color: @greenTerra700;
  border: 1px solid @greenTerra700;
  border-radius: 6px;

  &:hover:not(:disabled),
  &:focus:not(:disabled) {
    background-color: @greenTerra800;
    border-color: @greenTerra800;
  }

  &:disabled {
    color: @gray400;
    background-color: @gray100;
    border-color: @gray200;
    cursor: not-allowed;
  }
}

/* loading skeleton */
.grondverzet-skeleton {
  display: flex;
  flex-direction: column;
}

.grondverzet-skeleton-band {
  height: 42px;
  background-color: @gray50;
  border-bottom: 1px solid @gray200;
}

.grondverzet-skeleton-body {
  display: flex;
  align-items: stretch;
  min-height: 470px;
}

.grondverzet-skeleton-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1 1 @formWidth;
  padding: 20px;
}

.grondverzet-skeleton-row {
  display: grid;
  grid-template-columns: 190px 1fr;
  gap: 12px;
}

.grondverzet-skeleton-bar {
  height: 12px;
  background-color: @gray100;
  border-radius: 4px;
  animation: grondverzet-pulse 1.4s ease-in-out infinite;

  &--label {
    background-color: @gray200;
  }
}

.grondverzet-skeleton-map {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 @mapWidth;
  padding: 20px;
  font-size: 13px;
  text-align: center;
  color: @gray500;
  background-color: @gray100;
  border-left: 1px solid @gray200;
}

@keyframes grondverzet-pulse {
  0%,
  100% {
    opacity: 1;
  }

  50% {
    opacity: 0.55;
  }
}

@media (prefers-reduced-motion: reduce) {
  .grondverzet-skeleton-bar {
    animation: none;
  }
}

// Below this width the two columns no longer fit the popup (capped at 95vw), so stack them.
@media (max-width: 1180px) {
  .grondverzet-content {
    width: 640px;
  }

  /deep/ .popupContent {
    overflow-y: auto;
  }

  .grondverzet-band {
    flex-wrap: wrap;
    gap: 8px 20px;
  }

  .grondverzet-layout {
    flex-direction: column;
    min-height: 0;
  }

  .grondverzet-form {
    overflow: visible;
  }

  .grondverzet-map-pane {
    flex: 0 0 auto;
    height: 340px;
    border-left: none;
    border-top: 1px solid @gray200;
  }
}
</style>
